import fetcher, { FleetPaginated, FleetQueryParams } from '../fetcher';

export type MasterData = {
	id: number;
	value: string;
};

export type MasterDataSlug =
	| 'business-entity'
	| 'general-status'
	| 'general-position'
	| 'customer-pic-position'
	| 'company-management-position'
	| 'prospect-group-status'
	| 'source-order-detail'
	| 'segment-market'
	| 'order-type'
	| 'company-type'
	| 'document-type'
	| 'identity-type'
	| 'identity-card-type'
	| 'shareholder-type'
	| 'plafon-type'
	| 'financing-type'
	| 'financing-object'
	| 'financial-report-audit-status'
	| 'financial-report-period-type'
	| 'financial-report-period-type-detail'
	| 'citizenship-type'
	| 'reject-category'
	| 'sales-method'
	| 'payment-method'
	| 'office-ownership-status'
	| 'inventory-item-type'
	| 'material-ownership-type'
	| 'bank-statement-account-type'
	| 'bank-entity'
	| 'financing-type-detail'
	| 'confirmation-media'
	| 'slik-status'
	| 'slik-checking-status'
	| 'request-status'
	| 'reject-customer'
	| 'financing-limit-characteristic';

export type MasterDataCondition = {
	parentSlug: MasterDataSlug;
	parentDataId: number;
};

export const getMasterData = (
	slug: MasterDataSlug,
	additionalCondition?: MasterDataCondition | null,
) => {
	return fetcher<MasterData[]>({
		api: '/master-global-data/get',
		body: {
			slug,
			additionalCondition,
		},
	});
};

export type AddressMasterData = {
	cityName: string;
	districtName: string;
	locationName: string;
	postalCode: string;
	postalId: number;
	provinceName: string;
};

export const getAddressMasterData = (body: FleetQueryParams) => {
	return fetcher<FleetPaginated<AddressMasterData>>({
		api: '/master-address/get-list',
		body,
	});
};

export type MasterLookupItem = Record<string, unknown>;

export const getMasterLookupData = (endpoint: string, body: FleetQueryParams) => {
	const api = endpoint.replace(/^\/api\//, '/');
	return fetcher<FleetPaginated<MasterLookupItem>>({ api, body });
};

export type MasterCityItem = {
	id: number;
	name: string;
	provinceId: number;
	provinceName: string;
};

export type CountryMasterData = {
	code: string;
	id: number;
	name: string;
	value?: string;
};

export const getCountryMasterData = (pageNumber?: number, value?: string) => {
	return fetcher<FleetPaginated<CountryMasterData>>({
		api: '/master-country/get-list',
		body: {
			pagination: {
				pageSize: 10,
				pageNumber,
			},
			search: {
				by: 'name',
				value,
			},
		},
	});
};

export type IndustryMasterData = {
	id: number;
	ecosystem: string;
	industryClass: string;
	portofolioGuideline: string;
	sectorLevel1: string;
	sectorLevel2: string;
	sectorLevel3: string;
	sectorLevel4: string;
	sectorLevel5: string;
};

export const getIndustryMasterData = (body: FleetQueryParams) => {
	return fetcher<FleetPaginated<IndustryMasterData>>({
		api: '/master-industry-sectors/get-list',
		body,
	});
};

export const getMasterBankData = (body: FleetQueryParams) => {
	return fetcher<FleetPaginated<MasterData>>({
		api: '/master-bank/get-list',
		body,
	});
};

export type BankMasterData = MasterData & {
	buc: string;
	group: string;
};

export const getCommercialBankMasterData = (body: FleetQueryParams) => {
	return fetcher<FleetPaginated<BankMasterData>>({
		api: '/master-commercial-bank/get-list',
		body,
	});
};

export const getSupplierDealerMasterData = (body: FleetQueryParams) => {
	return fetcher<FleetPaginated<MasterData>>({
		api: '/dealer-supplier/get-list',
		body,
	});
};

export const getCorporateBankMasterData = (body: FleetQueryParams) => {
	return fetcher<FleetPaginated<BankMasterData>>({
		api: '/master-corporate-bank/get-list',
		body,
	});
};

export type MandiriBranchMasterData = {
	branchId: number;
	branchName: string;
};

export const getMandiriBranchesMasterData = (body: FleetQueryParams) => {
	return fetcher<FleetPaginated<MandiriBranchMasterData>>({
		api: '/mandiri-branches/get-list',
		body,
	});
};

export const getBrandMasterData = (body: FleetQueryParams) => {
	return fetcher<FleetPaginated<MasterData>>({
		api: '/master-brand/get-list',
		body,
	});
};

export const getBrandTypeMasterData = (body: FleetQueryParams) => {
	return fetcher<FleetPaginated<MasterData>>({
		api: '/master-brand-type/get-list',
		body,
	});
};

export const getDivisionMasterData = (body: FleetQueryParams) => {
	return fetcher<FleetPaginated<MasterData>>({
		api: '/master-division/get-list',
		body,
	});
};

export type BouwheerMasterData = {
	id: string;
	name: string;
	type: string;
	industryClass: string;
	industrySector: IndustryMasterData;
	isActive: string;
};

export const getBouwheerMasterData = (body: FleetQueryParams = {}) => {
	return fetcher<FleetPaginated<BouwheerMasterData>>({
		api: '/master-bouwheers/get-list',
		body,
	});
};

export type MasterSupplier = {
	id: string;
	name: string;
	address: string;
	phoneNo: string;
};

export const getMasterSupplierData = (supplierName: string) => {
	return fetcher<MasterSupplier[]>({
		api: '/master-suppliers/v2/search-supplier',
		body: {
			supplierName,
		},
	});
};

export type AssetMasterData = {
	code: string;
	name: string;
	categoryId: string;
	modelId: string;
	modelName: string;
};

export const searchAsset = (brandName: string) => {
	return fetcher<AssetMasterData[]>({
		api: '/master-assets/get-assets',
		body: {
			brandName,
		},
	});
};

export const getBrandList = (body: FleetQueryParams) => {
	return fetcher<FleetPaginated<AssetMasterData>>({
		api: '/master-assets/get-brands',
		body,
	});
};

export type AssetBrandMasterData = {
	id: string;
	name: string;
};

export const searchAssetBrand = (assetTypeId: number) => {
	return fetcher<AssetBrandMasterData[]>({
		api: '/master-assets/get-brands',
		body: {
			assetTypeId,
		},
	});
};

export type KaroseriCompany = {
	id: string;
	name: string;
	address: string;
	phoneNo: string;
};

export const getKaroseriCompanyList = (supplierName: string) => {
	return fetcher<KaroseriCompany[]>({
		api: '/master-suppliers/search-supplier-karoseri',
		body: {
			supplierName,
		},
	});
};

export type ProductMasterData = {
	id: string;
	name: string;
};

export const getProductList = (body: {
	pagination: { pageSize: number; pageNumber: number };
	search?: { by: string; value: string };
}) => {
	return fetcher<{
		totalCount: number;
		pageSize: number;
		currentPage: number;
		totalPages: number;
		items: ProductMasterData[];
	}>({
		api: '/master-products/search-product',
		body,
	});
};
